import { defineStore } from "pinia";
import {
  ActivitiesApi,
  VideosApi,
  type ActivitiesCreateDtoTypeEnum,
  type ActivitiesCreateQuizDtoSubTypeEnum,
  type ActivitiesUpdateDto,
  type ActivitiesUpdateQuizDto,
} from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { ActivityItemTypeEnum } from "~/utils/enums/activities.enums";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import type { IActivityOwner } from "~/utils/interfaces/activities-interfaces";

/**
 * orders' activities commands, whatever owns the activité: an LMS training or an evaluation template. The builders keep
 * their own state and feedback around them; each command throws on failure.
 */
export const useActivityEditorStore = defineStore("ActivityEditorStore", {
  actions: {
    async create(owner: IActivityOwner, type?: ActivitiesCreateDtoTypeEnum | null) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.createActivity({ ...owner, activitiesCreateDto: type ? { type } : {} });
    },

    async createQuiz(owner: IActivityOwner, activityId: string, subType: ActivitiesCreateQuizDtoSubTypeEnum) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.createActivityQuiz({ ...owner, activitiesCreateQuizDto: { activityId, subType } });
    },

    async update(owner: IActivityOwner, activityId: string, activitiesUpdateDto: ActivitiesUpdateDto) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.updateActivity({ ...owner, activityId, activitiesUpdateDto });
    },

    async updateQuiz(
      owner: IActivityOwner,
      activityId: string,
      quizId: string,
      activitiesUpdateQuizDto: ActivitiesUpdateQuizDto,
    ) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.updateActivityQuiz({ ...owner, activityId, quizId, activitiesUpdateQuizDto });
    },

    async duplicate(owner: IActivityOwner, activityId: string) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.duplicateActivity({ ...owner, activityId });
    },

    async uploadDocument(owner: IActivityOwner, activityId: string, file: File) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      return api.uploadActivityDocument({ ...owner, activityId, file });
    },

    async deleteVideo(videoId: string) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, VideosApi);
      return api.deleteVimeoVideo({ videoId });
    },

    // A template's activité takes its collected answers with it, which orders wants confirmed.
    async remove(owner: IActivityOwner, activityId: string) {
      const api = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      if (owner.itemType === ActivityItemTypeEnum.SESSION_FORM) {
        await api.deleteSessionFormActivity({ itemId: owner.itemId, activityId, confirmDeleteResponses: true });
        return;
      }
      await api.deleteActivity({ activityId });
    },
  },
});
